import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Box,
  Button,
  Container,
  Grid,
  Paper,
  Typography,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormControlLabel,
  Switch,
  Chip,
  Alert,
  CircularProgress,
  FormHelperText,
  Autocomplete,
  IconButton
} from '@mui/material';
import { 
  Save as SaveIcon,
  ArrowBack as ArrowBackIcon,
  CloudUpload as CloudUploadIcon,
  Delete as DeleteIcon,
  Add as AddIcon
} from '@mui/icons-material';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5001/api';

// List of genre options
const genreOptions = [
  'House', 'Deep House', 'Tech House', 'Techno', 'Trance', 'Minimal', 'Progressive House',
  'Disco', 'Nu Disco', 'Indie Dance', 'Electronica', 'Ambient', 'Downtempo',
  'Breaks', 'Drum & Bass', 'Dubstep', 'Trap', 'Hip Hop', 'R&B',
  'Pop', 'Rock', 'Indie', 'Alternative', 'Metal', 'Punk',
  'Jazz', 'Blues', 'Soul', 'Funk', 'Reggae', 'Dancehall',
  'Afrobeat', 'Latin', 'Classical'
];

const EditReleasePage = () => {
  const { releaseId } = useParams();
  const navigate = useNavigate();
  
  // Form state
  const [formData, setFormData] = useState({
    title: '',
    primaryGenre: '',
    secondaryGenres: [],
    releaseYear: new Date().getFullYear(),
    releaseDate: new Date(),
    description: '',
    explicit: false,
    label: '',
    catalogNumber: '',
    type: ''
  });
  
  // Marketing info state (optional section)
  const [marketingInfo, setMarketingInfo] = useState({
    preSaveDateEnabled: false,
    preOrderDateEnabled: false,
    preSaveDate: null,
    preOrderDate: null,
    marketingNotes: ''
  });
  
  // Cover art state
  const [coverArt, setCoverArt] = useState(null);
  const [coverArtPreview, setCoverArtPreview] = useState(null);
  
  // UI state
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(false);
  const [tracks, setTracks] = useState([]);
  const [editingTrackId, setEditingTrackId] = useState(null);
  const [trackFormData, setTrackFormData] = useState({
    title: '',
    primaryGenre: '',
    explicit: false,
    featuredArtists: []
  });
  
  useEffect(() => {
    const fetchReleaseForEdit = async () => {
      try {
        setLoading(true);
        setError(null);
        
        const token = localStorage.getItem('token');
        if (!token) {
          navigate('/login');
          return;
        }
        
        const config = {
          headers: { Authorization: `Bearer ${token}` }
        };
        
        // Use the special endpoint for fetching releases for editing
        const response = await axios.get(`${API_URL}/releases/${releaseId}/edit`, config);
        const releaseData = response.data;
        
        // Set form data with release details
        setFormData({
          title: releaseData.title || '',
          primaryGenre: releaseData.primaryGenre || '',
          secondaryGenres: releaseData.secondaryGenres || [],
          releaseYear: releaseData.releaseYear || new Date().getFullYear(),
          releaseDate: releaseData.releaseDate ? new Date(releaseData.releaseDate) : new Date(),
          description: releaseData.description || '',
          explicit: releaseData.explicit || false,
          label: releaseData.label || '',
          catalogNumber: releaseData.catalogNumber || '',
          type: releaseData.type || ''
        });
        
        // Set marketing info if available
        if (releaseData.marketingInfo) {
          setMarketingInfo({
            preSaveDateEnabled: releaseData.marketingInfo.preSaveDateEnabled || false,
            preOrderDateEnabled: releaseData.marketingInfo.preOrderDateEnabled || false,
            preSaveDate: releaseData.marketingInfo.preSaveDate ? new Date(releaseData.marketingInfo.preSaveDate) : null,
            preOrderDate: releaseData.marketingInfo.preOrderDate ? new Date(releaseData.marketingInfo.preOrderDate) : null,
            marketingNotes: releaseData.marketingInfo.marketingNotes || ''
          });
        }
        
        // Set cover art preview if available
        if (releaseData.coverArt) {
          // Check if coverArt is already a full URL (Google Cloud Storage URL)
          if (releaseData.coverArt.startsWith('http')) {
            setCoverArtPreview(releaseData.coverArt);
          } else {
            setCoverArtPreview(`${API_URL}${releaseData.coverArt}`);
          }
        }
        
        // Set tracks data
        if (releaseData.tracks && Array.isArray(releaseData.tracks)) {
          setTracks(releaseData.tracks);
        }
        
        setLoading(false);
      } catch (err) {
        setLoading(false);
        setError(err.response?.data?.message || 'Failed to load release for editing');
      }
    };
    
    fetchReleaseForEdit();
  }, [releaseId, navigate]);
  
  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };
  
  // Handle marketing info changes - TEMPORARILY COMMENTED OUT
  // const handleMarketingChange = (e) => {
  //   const { name, value, type, checked } = e.target;
  //   setMarketingInfo(prev => ({
  //     ...prev,
  //     [name]: type === 'checkbox' ? checked : value
  //   }));
  // };
  
  // Handle secondary genres change
  const handleSecondaryGenresChange = (event, newValue) => {
    setFormData(prev => ({
      ...prev,
      secondaryGenres: newValue
    }));
  };
  
  // Handle cover art upload
  const handleCoverArtChange = (e) => {
    if (e.target.files && e.target.files[0]) {
      const file = e.target.files[0];
      setCoverArt(file);
      
      // Create a preview
      const reader = new FileReader();
      reader.onload = (event) => {
        setCoverArtPreview(event.target.result);
      };
      reader.readAsDataURL(file);
    }
  };
  
  // Handle form submission
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(false);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Create form data for submission
      const submitData = new FormData();
      
      // Add basic form fields
      Object.keys(formData).forEach(key => {
        if (key === 'secondaryGenres') {
          // Handle arrays specially
          submitData.append(key, JSON.stringify(formData[key]));
        } else {
          submitData.append(key, formData[key]);
        }
      });
      
      // Add marketing info
      submitData.append('marketingInfo', JSON.stringify(marketingInfo));
      
      // Add cover art if changed
      if (coverArt) {
        submitData.append('coverArt', coverArt);
      }
      
      // Send update request
      await axios.put(
        `${API_URL}/releases/${releaseId}`,
        submitData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setSuccess(true);
      setSubmitting(false);
      
      // Redirect back to release page after short delay
      setTimeout(() => {
        navigate(`/release/${releaseId}`); // Fixed path from "releases" to "release"
      }, 1500);
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update release');
      setSubmitting(false);
    }
  };
  
  // Handle track edit button click
  const handleEditTrack = (track) => {
    setEditingTrackId(track._id);
    setTrackFormData({
      title: track.title,
      primaryGenre: track.primaryGenre,
      explicit: track.explicit,
      featuredArtists: track.featuredArtists || [{
        name: '',
        role: 'main'
      }]
    });
  };
  
  // Handle track form input changes
  const handleTrackInputChange = (e) => {
    const { name, value, type, checked } = e.target;
    setTrackFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle featured artists changes
  const handleFeaturedArtistChange = (index, field, value) => {
    const updatedArtists = [...trackFormData.featuredArtists];
    updatedArtists[index][field] = value;
    setTrackFormData(prev => ({
      ...prev,
      featuredArtists: updatedArtists
    }));
  };

  const addFeaturedArtist = () => {
    setTrackFormData(prev => ({
      ...prev,
      featuredArtists: [...prev.featuredArtists, { name: '', role: 'featured' }]
    }));
  };

  const removeFeaturedArtist = (index) => {
    const artists = trackFormData.featuredArtists;
    const mainArtists = artists.filter(a => a.role === 'main');
    
    // Don't allow removing if it's the only main artist
    if (artists[index].role === 'main' && mainArtists.length === 1) {
      return; // Don't remove the last main artist
    }
    
    const updatedArtists = trackFormData.featuredArtists.filter((_, i) => i !== index);
    setTrackFormData(prev => ({
      ...prev,
      featuredArtists: updatedArtists
    }));
  };
  
  // Handle track update submission
  const handleTrackSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setSubmitting(true);
      setError(null);
      setSuccess(false);
      
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }
      
      // Create form data for track update
      const trackData = new FormData();
      trackData.append('title', trackFormData.title);
      trackData.append('primaryGenre', trackFormData.primaryGenre);
      trackData.append('explicit', trackFormData.explicit);
      trackData.append('featuredArtists', JSON.stringify(trackFormData.featuredArtists));
      
      // Send track update request
      await axios.put(
        `${API_URL}/tracks/${editingTrackId}`,
        trackData,
        {
          headers: {
            Authorization: `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        }
      );
      
      setSuccess(true);
      setSubmitting(false);
      
      // Refresh tracks data
      const response = await axios.get(`${API_URL}/releases/${releaseId}/edit`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setTracks(response.data.tracks);
      
      // Close track edit mode
      setEditingTrackId(null);
      setTrackFormData({
        title: '',
        primaryGenre: '',
        explicit: false,
        featuredArtists: []
      });
      
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to update track');
      setSubmitting(false);
    }
  };
  
  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
        <CircularProgress />
      </Box>
    );
  }
  
  return (
    <Container maxWidth="lg" sx={{ mt: 4, mb: 6 }}>
      <Paper sx={{ p: 4 }}>
        {/* Page header */}
        <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Typography variant="h4" component="h1">
            Edit Release
          </Typography>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate(`/release/${releaseId}`)}
          >
            Back to Release
          </Button>
        </Box>
        
        {/* Status messages */}
        {error && (
          <Alert severity="error" sx={{ mb: 4 }}>
            {error}
          </Alert>
        )}
        
        {success && (
          <Alert severity="success" sx={{ mb: 4 }}>
            Release updated successfully!
          </Alert>
        )}
        
        {/* Edit form */}
        <form onSubmit={handleSubmit}>
          <Grid container spacing={4}>
            {/* Cover Art Section */}
            <Grid item xs={12} md={4}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{
                  height: '100%',
                  p: 2,
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  justifyContent: 'center',
                  mb: { xs: 2, md: 0 }
                }}
              >
                <Typography variant="h6" gutterBottom>
                  Cover Art
                </Typography>
                
                {/* Cover preview */}
                <Box
                  sx={{
                    width: '100%',
                    mb: 2,
                    mt: 2,
                    aspectRatio: '1/1',
                    backgroundColor: 'rgba(0,0,0,0.1)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    borderRadius: 1,
                    overflow: 'hidden'
                  }}
                >
                  {coverArtPreview ? (
                    <Box
                      component="img"
                      src={coverArtPreview}
                      alt="Cover Art"
                      sx={{
                        width: '100%',
                        height: '100%',
                        objectFit: 'cover'
                      }}
                    />
                  ) : (
                    <Typography variant="body2" color="text.secondary">
                      No cover art
                    </Typography>
                  )}
                </Box>
                
                {/* Upload button */}
                <Button
                  component="label"
                  variant="contained"
                  startIcon={<CloudUploadIcon />}
                  sx={{ mt: 2 }}
                >
                  Change Cover
                  <input
                    type="file"
                    accept="image/*"
                    hidden
                    onChange={handleCoverArtChange}
                  />
                </Button>
                
                <Typography variant="caption" color="text.secondary" sx={{ mt: 1, textAlign: 'center' }}>
                  Recommended: 3000x3000 pixels, square format
                </Typography>
              </Paper>
            </Grid>
            
            {/* Release Info Section */}
            <Grid item xs={12} md={8}>
              <Grid container spacing={3}>
                {/* Basic Info */}
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom>
                    Basic Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={8}>
                  <TextField
                    label="Release Title"
                    name="title"
                    value={formData.title}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={4}>
                  <FormControl fullWidth sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    },
                    '& .MuiInputLabel-root': {
                      color: 'text.secondary'
                    },
                    '& .MuiSelect-select.Mui-disabled': {
                      color: '#666666 !important',
                      WebkitTextFillColor: '#666666 !important',
                      opacity: '1 !important'
                    },
                    '& .MuiInputBase-input.Mui-disabled': {
                      color: '#666666 !important',
                      WebkitTextFillColor: '#666666 !important',
                      opacity: '1 !important'
                    },
                    '& .MuiOutlinedInput-input.Mui-disabled': {
                      color: '#666666 !important',
                      WebkitTextFillColor: '#666666 !important',
                      opacity: '1 !important'
                    }
                  }}>
                    <InputLabel id="type-label">Release Type</InputLabel>
                    <Select
                      labelId="type-label"
                      label="Release Type"
                      name="type"
                      value={formData.type}
                      onChange={handleInputChange}
                      disabled
                    >
                      <MenuItem value="single">Single</MenuItem>
                      <MenuItem value="ep">EP</MenuItem>
                      <MenuItem value="album">Album</MenuItem>
                    </Select>
                    <FormHelperText>Type cannot be changed after upload</FormHelperText>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}>
                    <InputLabel id="primary-genre-label">Primary Genre</InputLabel>
                    <Select
                      labelId="primary-genre-label"
                      label="Primary Genre"
                      name="primaryGenre"
                      value={formData.primaryGenre}
                      onChange={handleInputChange}
                      required
                    >
                      {genreOptions.map(genre => (
                        <MenuItem key={genre} value={genre}>{genre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <Autocomplete
                    multiple
                    options={genreOptions.filter(genre => genre !== formData.primaryGenre)}
                    value={formData.secondaryGenres}
                    onChange={handleSecondaryGenresChange}
                    renderTags={(value, getTagProps) =>
                      value.map((option, index) => (
                        <Chip
                          label={option}
                          {...getTagProps({ index })}
                          size="small"
                        />
                      ))
                    }
                    renderInput={(params) => (
                      <TextField
                        {...params}
                        label="Secondary Genres"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            '& fieldset': { borderColor: '#666666' },
                            '&:hover fieldset': { borderColor: '#666666' },
                            '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                          }
                        }}
                      />
                    )}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Label"
                    name="label"
                    value={formData.label}
                    onChange={handleInputChange}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Catalog Number"
                    name="catalogNumber"
                    value={formData.catalogNumber}
                    onChange={handleInputChange}
                    fullWidth
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <TextField
                    label="Release Year"
                    name="releaseYear"
                    type="number"
                    value={formData.releaseYear}
                    onChange={handleInputChange}
                    fullWidth
                    required
                    InputProps={{
                      inputProps: {
                        min: 1900,
                        max: new Date().getFullYear() + 5
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <LocalizationProvider dateAdapter={AdapterDateFns}>
                    <DatePicker
                      label="Release Date"
                      value={formData.releaseDate}
                      onChange={(newDate) => setFormData(prev => ({ ...prev, releaseDate: newDate }))}
                      slotProps={{
                        textField: {
                          fullWidth: true,
                          sx: {
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#666666' },
                              '&:hover fieldset': { borderColor: '#666666' },
                              '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                            }
                          }
                        }
                      }}
                    />
                  </LocalizationProvider>
                </Grid>
                
                <Grid item xs={12}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="explicit"
                        checked={formData.explicit}
                        onChange={handleInputChange}
                      />
                    }
                    label="Explicit Content"
                  />
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Description"
                    name="description"
                    value={formData.description}
                    onChange={handleInputChange}
                    fullWidth
                    multiline
                    rows={4}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }}
                  />
                </Grid>
                
                {/* Marketing Info Section - TEMPORARILY HIDDEN FOR DISTRIBUTION */}
                {/* 
                <Grid item xs={12}>
                  <Typography variant="h6" gutterBottom sx={{ mt: 2 }}>
                    Marketing Information
                  </Typography>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="preSaveDateEnabled"
                        checked={marketingInfo.preSaveDateEnabled}
                        onChange={handleMarketingChange}
                      />
                    }
                    label="Enable Pre-Save Date"
                  />
                  {marketingInfo.preSaveDateEnabled && (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Pre-Save Date"
                        value={marketingInfo.preSaveDate}
                        onChange={(newDate) => setMarketingInfo(prev => ({ ...prev, preSaveDate: newDate }))}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              mt: 2,
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#666666' },
                                '&:hover fieldset': { borderColor: '#666666' },
                                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="preOrderDateEnabled"
                        checked={marketingInfo.preOrderDateEnabled}
                        onChange={handleMarketingChange}
                      />
                    }
                    label="Enable Pre-Order Date"
                  />
                  {marketingInfo.preOrderDateEnabled && (
                    <LocalizationProvider dateAdapter={AdapterDateFns}>
                      <DatePicker
                        label="Pre-Order Date"
                        value={marketingInfo.preOrderDate}
                        onChange={(newDate) => setMarketingInfo(prev => ({ ...prev, preOrderDate: newDate }))}
                        slotProps={{
                          textField: {
                            fullWidth: true,
                            sx: {
                              mt: 2,
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#666666' },
                                '&:hover fieldset': { borderColor: '#666666' },
                                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                              }
                            }
                          }
                        }}
                      />
                    </LocalizationProvider>
                  )}
                </Grid>
                
                <Grid item xs={12}>
                  <TextField
                    label="Marketing Notes"
                    name="marketingNotes"
                    value={marketingInfo.marketingNotes}
                    onChange={handleMarketingChange}
                    fullWidth
                    multiline
                    rows={3}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }}
                  />
                </Grid>
                */}
              </Grid>
            </Grid>
            
            {/* Tracks Section - Read Only */}
            <Grid item xs={12}>
              <Paper
                elevation={0}
                variant="outlined"
                sx={{ p: 3, mt: 3 }}
              >
                <Typography variant="h6" gutterBottom>
                  Tracks
                </Typography>
                <Typography variant="body2" color="text.secondary" gutterBottom>
                  Tracks cannot be reordered after the initial upload, but you can edit individual track details by clicking the Edit button.
                </Typography>
                
                {tracks.length > 0 ? (
                  <Box sx={{ mt: 2 }}>
                    {tracks.map((track, index) => (
                      <Box
                        key={track._id}
                        sx={{
                          p: 2,
                          mb: 1,
                          border: '1px solid',
                          borderColor: 'divider',
                          borderRadius: 1,
                          display: 'flex',
                          alignItems: 'center'
                        }}
                      >
                        <Typography variant="body1" sx={{ width: '40px' }}>
                          {(index + 1).toString().padStart(2, '0')}.
                        </Typography>
                        <Box sx={{ flex: 1 }}>
                          <Typography variant="body1">
                            {track.title}
                          </Typography>
                          <Typography variant="body2" color="text.secondary">
                            {track.trackType} • {track.primaryGenre}
                          </Typography>
                        </Box>
                        {/* Removed "Details" button to prevent navigation to individual track page */}
                        <Button
                          variant="outlined"
                          size="small"
                          onClick={() => handleEditTrack(track)}
                        >
                          Edit
                        </Button>
                      </Box>
                    ))}
                  </Box>
                ) : (
                  <Typography variant="body1" color="text.secondary" align="center" sx={{ my: 4 }}>
                    No tracks found
                  </Typography>
                )}
              </Paper>
            </Grid>
            
            {/* Submit Button */}
            <Grid item xs={12}>
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  size="large"
                  startIcon={<SaveIcon />}
                  disabled={submitting}
                  sx={{ px: 4, py: 1 }}
                >
                  {submitting ? 'Saving...' : 'Save Changes'}
                </Button>
              </Box>
            </Grid>
          </Grid>
        </form>
        
        {/* Track Edit Form - Conditional Rendering */}
        {editingTrackId && (
          <Paper sx={{ p: 3, mt: 4 }}>
            <Typography variant="h6" gutterBottom>
              Edit Track
            </Typography>
            
            <form onSubmit={handleTrackSubmit}>
              <Grid container spacing={3}>
                <Grid item xs={12}>
                  <TextField
                    label="Track Title"
                    name="title"
                    value={trackFormData.title}
                    onChange={handleTrackInputChange}
                    fullWidth
                    required
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        '& fieldset': { borderColor: '#666666' },
                        '&:hover fieldset': { borderColor: '#666666' },
                        '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                      }
                    }}
                  />
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControl fullWidth sx={{
                    '& .MuiOutlinedInput-root': {
                      '& fieldset': { borderColor: '#666666' },
                      '&:hover fieldset': { borderColor: '#666666' },
                      '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                    }
                  }}>
                    <InputLabel id="track-primary-genre-label">Primary Genre</InputLabel>
                    <Select
                      labelId="track-primary-genre-label"
                      label="Primary Genre"
                      name="primaryGenre"
                      value={trackFormData.primaryGenre}
                      onChange={handleTrackInputChange}
                      required
                    >
                      {genreOptions.map(genre => (
                        <MenuItem key={genre} value={genre}>{genre}</MenuItem>
                      ))}
                    </Select>
                  </FormControl>
                </Grid>
                
                <Grid item xs={12} sm={6}>
                  <FormControlLabel
                    control={
                      <Switch
                        name="explicit"
                        checked={trackFormData.explicit}
                        onChange={handleTrackInputChange}
                      />
                    }
                    label="Explicit Content"
                  />
                </Grid>

                {/* Featured Artists Section */}
                <Grid item xs={12}>
                  <Typography variant="subtitle2" gutterBottom>
                    Featured Artists
                  </Typography>
                  {trackFormData.featuredArtists.map((artist, index) => (
                    <Paper key={index} sx={{ p: 2, mb: 2, bgcolor: 'background.default' }}>
                      <Grid container spacing={2} alignItems="center">
                        <Grid item xs={12} sm={6}>
                          <TextField
                            fullWidth
                            label="Artist Name"
                            value={artist.name || ''}
                            onChange={(e) => handleFeaturedArtistChange(index, 'name', e.target.value)}
                            required
                            sx={{
                              '& .MuiOutlinedInput-root': {
                                '& fieldset': { borderColor: '#666666' },
                                '&:hover fieldset': { borderColor: '#666666' },
                                '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                              }
                            }}
                          />
                        </Grid>
                        <Grid item xs={12} sm={4}>
                          <FormControl fullWidth sx={{
                            '& .MuiOutlinedInput-root': {
                              '& fieldset': { borderColor: '#666666' },
                              '&:hover fieldset': { borderColor: '#666666' },
                              '&.Mui-focused fieldset': { borderColor: '#a187f2' }
                            }
                          }}>
                            <InputLabel>Role</InputLabel>
                            <Select
                              value={artist.role || 'main'}
                              onChange={(e) => handleFeaturedArtistChange(index, 'role', e.target.value)}
                              label="Role"
                            >
                              <MenuItem value="main">Main</MenuItem>
                              <MenuItem value="featured">Featured</MenuItem>
                              <MenuItem value="remixer">Remixer</MenuItem>
                              <MenuItem value="vocalist">Vocalist</MenuItem>
                              <MenuItem value="producer">Producer</MenuItem>
                            </Select>
                          </FormControl>
                        </Grid>
                        <Grid item xs={12} sm={2}>
                          <IconButton
                            color="error"
                            onClick={() => removeFeaturedArtist(index)}
                            disabled={
                              artist.role === 'main' && 
                              trackFormData.featuredArtists.filter(a => a.role === 'main').length === 1
                            }
                          >
                            <DeleteIcon />
                          </IconButton>
                          {artist.role === 'main' && 
                           trackFormData.featuredArtists.filter(a => a.role === 'main').length === 1 && (
                            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', textAlign: 'center', mt: 0.5 }}>
                              Last main artist
                            </Typography>
                          )}
                        </Grid>
                      </Grid>
                    </Paper>
                  ))}
                  <Button
                    variant="outlined"
                    startIcon={<AddIcon />}
                    onClick={addFeaturedArtist}
                    sx={{ mt: 1 }}
                  >
                    Add Featured Artist
                  </Button>
                </Grid>
                
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={() => setEditingTrackId(null)}
                      sx={{ mr: 2 }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      variant="contained"
                      size="large"
                      startIcon={<SaveIcon />}
                      disabled={submitting}
                    >
                      {submitting ? 'Saving...' : 'Save Track'}
                    </Button>
                  </Box>
                </Grid>
              </Grid>
            </form>
          </Paper>
        )}
      </Paper>
    </Container>
  );
};

export default EditReleasePage;